import React, {createContext, ReactNode, useContext, useEffect, useState} from 'react';
import {SearchCriteria} from '../../gallery/types/SearchCriteria';
import preferencesService from '../services/preferencesService';

interface SearchContextType {
  searchCriteria: SearchCriteria;
  updateSearchCriteria: (criteria: SearchCriteria) => void;
  setSearchTitle: (title: string) => void;
  setSearchOptions: (exactSearch?: boolean, onlyTitles?: boolean) => void;
  resetAndSetCollection: (collectionId: string) => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

interface SearchProviderProps {
  children: ReactNode;
}

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>(() => {
    return preferencesService.getGalleryFilters();
  });

  useEffect(() => {
    preferencesService.updateGalleryFilters(searchCriteria);
  }, [searchCriteria]);

  const updateSearchCriteria = (criteria: SearchCriteria) => {
    setSearchCriteria(criteria);
  };

  const setSearchTitle = (title: string) => {
    setSearchCriteria(prev => ({
      ...prev,
      searchTerms: title.trim() || undefined
    }));
  };

  const setSearchOptions = (exactSearch?: boolean, onlyTitles?: boolean) => {
    setSearchCriteria(prev => ({
      ...prev,
      exactSearch,
      onlyTitles
    }));
  };

  const resetAndSetCollection = (collectionId: string) => {
    setSearchCriteria({
      collections: [collectionId]
    });
  };

  return (
    <SearchContext.Provider value={{
      searchCriteria,
      updateSearchCriteria,
      setSearchTitle,
      setSearchOptions,
      resetAndSetCollection
    }}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = (): SearchContextType => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};
